import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';
import { getQuizQuestions } from '../utils/quizEval';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import ModuleViewer, { prefetchModuleSlides } from './ModuleViewer';
import ModernCertificate from './ModernCertificate';
import ProcessSystemsView from './ProcessSystemsView';
import ExamStatusBanner from './ExamStatusBanner';
import AppointmentBooking from './AppointmentBooking';
import AppointmentList from './AppointmentList';

const ProgressBar = ({ current, total, color = 'blue' }) => {
  const percentage = total > 0 ? (current / total) * 100 : 0;
  const isCompleted = percentage === 100;
  
  // Define color styles to ensure they are included in build
  const colorStyles = {
    blue: isCompleted ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-blue-500 to-blue-600',
    green: 'bg-gradient-to-r from-green-500 to-green-600',
    red: 'bg-gradient-to-r from-red-500 to-red-600',
    yellow: 'bg-gradient-to-r from-yellow-500 to-yellow-600',
    purple: 'bg-gradient-to-r from-purple-500 to-purple-600',
    orange: 'bg-gradient-to-r from-orange-500 to-orange-600',
    pink: 'bg-gradient-to-r from-pink-500 to-pink-600',
    indigo: 'bg-gradient-to-r from-indigo-500 to-indigo-600',
  };
  
  const bgColorClass = colorStyles[color] || colorStyles.blue;
  
  return (
    <div className="w-full bg-gray-200 rounded-full h-4 relative">
      <div 
        className={`h-4 rounded-full transition-all duration-500 ease-out ${bgColorClass}`}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
      {isCompleted && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-medium text-white">✓ 100%</span>
        </div>
      )}
    </div>
  );
};

const CategoryCard = ({ category, onSelect, readOnly = false }) => {
  const completionRate = category.modules_count > 0 ? (category.completed_modules / category.modules_count) * 100 : 0;
  const isLocked = !category.is_unlocked;
  const isMandatory = category.is_mandatory !== false; // Default to true if not specified
  const isViewOnly = readOnly || (!isMandatory && category.is_unlocked); // View-only if readOnly role OR not mandatory but unlocked
  
  return (
    <div 
      onClick={() => category.is_unlocked && onSelect(category)}
      className={`bg-white rounded-xl shadow-lg p-6 transition-all duration-200 relative ${
        isLocked 
          ? 'opacity-50 cursor-not-allowed pointer-events-none' 
          : isViewOnly
            ? 'cursor-pointer hover:shadow-lg border-2 border-blue-200 bg-blue-50'
            : 'cursor-pointer hover:shadow-xl hover:scale-105'
      } border-2 ${
        category.is_unlocked && !isViewOnly ? 'border-transparent hover:border-blue-200' : 'border-gray-200'
      }`}
      title={isLocked ? (category.next_unlock_info || "Diese Kategorie wird freigeschaltet, sobald du die vorherige vollständig abgeschlossen hast.") : ""}
    >
      {/* View-Only Badge */}
      {isViewOnly && !isLocked && (
        <div className="absolute top-4 right-4 z-10">
          <div className="bg-blue-500 rounded-full px-3 py-1 flex items-center space-x-1 shadow-md">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="text-xs font-bold text-white">Nur Einsicht</span>
          </div>
        </div>
      )}
      
      {/* Lock Icon Overlay */}
      {isLocked && (
        <div className="absolute top-4 right-4 z-10">
          <div className="bg-slate-600 bg-opacity-80 rounded-full p-2">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        </div>
      )}
      
      {/* Completion Badge */}
      {completionRate === 100 && isMandatory && (
        <div className="absolute top-4 left-4 z-10">
          <div className="bg-green-500 rounded-full p-1">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
      )}
      
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          {/* Enhanced Icon Container - using admin-set icons */}
          <div 
            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg transform transition-transform duration-200 ${
              isLocked ? 'grayscale scale-95' : 'hover:scale-110'
            }`}
            style={{ 
              backgroundColor: category.color,
              boxShadow: `0 8px 25px -8px ${category.color}40`
            }}
          >
            {category.icon || category.title[0]}
          </div>
          <div className="flex-1">
            <h3 className={`text-xl font-bold mb-1 ${isLocked ? 'text-gray-500' : 'text-gray-900'}`}>
              {category.title}
            </h3>
            <p className={`text-sm leading-relaxed ${isLocked ? 'text-gray-400' : 'text-gray-600'} line-clamp-3`}>
              {category.description}
            </p>
          </div>
        </div>
        
        {/* Order Number */}
        <div className={`text-xs px-3 py-1 rounded-full font-bold ${
          isLocked 
            ? 'bg-gray-100 text-gray-400' 
            : 'bg-blue-100 text-blue-600'
        }`}>
          #{category.order || 1}
        </div>
      </div>
      
      {!isViewOnly ? (
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span className="font-medium">Fortschritt</span>
            <span className="font-semibold">{category.completed_modules}/{category.modules_count} Module</span>
          </div>
          <ProgressBar 
            current={category.completed_modules} 
            total={category.modules_count}
            color={isLocked ? 'gray' : (category.color?.replace('#', '') || 'blue')}
          />
        </div>
      ) : (
        <div className="mb-4">
          <div className="flex justify-between text-sm text-blue-600 mb-2">
            <span className="font-medium flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Informativ
            </span>
            <span className="font-semibold">{category.modules_count} Module verfügbar</span>
          </div>
          <div className="bg-blue-200 h-2 rounded-full"></div>
        </div>
      )}
      
      <div className="flex justify-between items-center">
        {isViewOnly ? (
          <>
            <span className="text-sm font-medium text-blue-600">
              Zur Information einsehbar
            </span>
            <span className="text-blue-600 font-bold text-sm flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Einsicht
            </span>
          </>
        ) : (
          <>
            <span className={`text-sm font-medium ${isLocked ? 'text-gray-400' : 'text-gray-500'}`}>
              {completionRate.toFixed(0)}% abgeschlossen
            </span>
            {completionRate === 100 && (
              <span className="text-green-500 font-bold text-sm flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                </svg>
                Abgeschlossen
              </span>
            )}
            {isLocked && (
              <span className="text-gray-400 font-medium text-sm flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z"/>
                </svg>
                Gesperrt
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const ModuleCard = ({ module, onStart, onComplete }) => {
  const isLocked = !module.is_unlocked;
  const isCompleted = module.completion_status === 'completed';
  const inProgress = module.completion_status === 'in_progress';
  
  const getContentTypeIcon = (type) => {
    switch(type) {
      case 'video': return '🎥';
      case 'quiz': return '📝';
      case 'pdf': return '📄';
      default: return '📖';
    }
  };
  
  const getContentTypeColor = (type) => {
    if (isLocked) {
      return 'text-gray-400 bg-gray-100';
    }
    switch(type) {
      case 'video': return 'text-red-600 bg-red-50';
      case 'quiz': return 'text-green-600 bg-green-50';
      case 'pdf': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };
  
  return (
    <div 
      className={`bg-white rounded-xl shadow-lg p-6 transition-all duration-300 relative ${
        isLocked 
          ? 'opacity-60 cursor-not-allowed border-2 border-gray-200' 
          : 'hover:shadow-xl border-2 border-transparent hover:border-blue-200'
      }`}
      title={isLocked ? (module.next_unlock_info || "Dieses Modul wird freigeschaltet, sobald du das vorherige Modul abgeschlossen hast.") : ""}
    >
      {/* Lock Icon Overlay */}
      {isLocked && (
        <div className="absolute top-4 right-4 z-10">
          <div className="bg-gray-500 bg-opacity-80 rounded-full p-2">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        </div>
      )}
      
      {/* Completion Badge */}
      {isCompleted && (
        <div className="absolute top-4 left-4 z-10">
          <div className="bg-green-500 rounded-full p-1">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
      )}
      
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className={`text-2xl ${isLocked ? 'grayscale' : ''}`}>
              {getContentTypeIcon(module.content_type)}
            </span>
            <h3 className={`text-lg font-semibold ${isLocked ? 'text-gray-500' : 'text-gray-900'}`}>
              {module.title}
            </h3>
            {/* Module Order Badge */}
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
              isLocked 
                ? 'bg-gray-100 text-gray-400' 
                : 'bg-blue-100 text-blue-600'
            }`}>
              #{module.order || 1}
            </span>
          </div>
          <p className={`mb-3 ${isLocked ? 'text-gray-400' : 'text-gray-600'}`}>
            {module.description}
          </p>
          
          <div className="flex items-center space-x-4 text-sm">
            <span className={`px-2 py-1 rounded-full ${getContentTypeColor(module.content_type)}`}>
              {module.content_type.toUpperCase()}
            </span>
            {module.duration_minutes && (
              <span className={`flex items-center space-x-1 ${isLocked ? 'text-gray-400' : 'text-gray-500'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{module.duration_minutes} Min</span>
              </span>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex justify-between items-center">
        {isCompleted ? (
          <div className="flex items-center space-x-2 text-green-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-semibold">Abgeschlossen</span>
          </div>
        ) : inProgress ? (
          <div className="flex items-center space-x-2 text-yellow-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-semibold">In Bearbeitung</span>
          </div>
        ) : isLocked ? (
          <div className="flex items-center space-x-2 text-gray-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="font-medium">Gesperrt</span>
          </div>
        ) : (
          <div className="text-gray-500">
            Bereit zum Starten
          </div>
        )}
        
        <div className="flex space-x-2">
          {!isLocked && (
            <button
              onClick={() => onStart(module)}
              onMouseEnter={() => module.content_type === 'pdf' && prefetchModuleSlides(module.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isCompleted 
                  ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isCompleted ? 'Erneut ansehen' : inProgress ? 'Fortsetzen' : 'Starten'}
            </button>
          )}
          {/* REMOVED: Direct completion button - modules must be opened to complete */}
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ showHeader = true, showNavigation = true, initialPage = 'dashboard', onPageChange = null }) => {
  const { user, refreshUser } = useAuth();
  const isReadOnly = user?.role === 'vertriebsleiter'; // Vertriebsleiter sees modules read-only
  const [categories, setCategories] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [showModuleViewer, setShowModuleViewer] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUnlockNotification, setShowUnlockNotification] = useState(false);
  const [newlyUnlockedCategories, setNewlyUnlockedCategories] = useState([]);
  const [rankData, setRankData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [prevBadgeCount, setPrevBadgeCount] = useState(null);
  
  // Certificate states
  const [certificateStatus, setCertificateStatus] = useState(null);
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificateLoading, setCertificateLoading] = useState(false);
  
  // Admin Certificate Preview states
  const [showAdminCertificatePreview, setShowAdminCertificatePreview] = useState(false);
  
  // Category PDFs state for Vertriebler
  const [categoryPdfs, setCategoryPdfs] = useState({});
  
  // Process & Systems navigation state
  const [currentPage, setCurrentPage] = useState(initialPage); // 'dashboard' or 'processes'
  const [selectedProcessSystem, setSelectedProcessSystem] = useState(null);
  
  // Academy Exam states
  const [examBookingUrl, setExamBookingUrl] = useState('');
  const [examStatus, setExamStatus] = useState(null);
  const [showAppointmentBooking, setShowAppointmentBooking] = useState(false);
  const [showMyAppointments, setShowMyAppointments] = useState(false);
  const [userAppointments, setUserAppointments] = useState([]);
  
  // Handle page changes with optional callback
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Refresh exam status when navigating back to dashboard
    if (page === 'dashboard') {
      fetchDashboardData();
    }
    if (onPageChange) {
      onPageChange(page);
    }
  };

  // Listen for bell notification category clicks from Header
  useEffect(() => {
    const handler = () => handlePageChange('dashboard');
    window.addEventListener('navigateToDashboard', handler);
    return () => window.removeEventListener('navigateToDashboard', handler);
  }, []);

  // Listen for appointment notification clicks from Header → open exam booking
  useEffect(() => {
    const handler = () => {
      setShowMyAppointments(false); // show booking form, not list
      handlePageChange('appointments');
    };
    window.addEventListener('navigateToExamBooking', handler);
    return () => window.removeEventListener('navigateToExamBooking', handler);
  }, []);

  // Handle mobile back button via popstate
  useEffect(() => {
    const handlePopState = (event) => {
      // If we were viewing a category detail, go back to category list
      if (selectedCategory) {
        setSelectedCategory(null);
        fetchDashboardData();
      }
      if (selectedProcessSystem) {
        setSelectedProcessSystem(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [selectedCategory, selectedProcessSystem]);
  
  // Upload states for Process Systems
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  // Modal states for Process Systems
  const [showSystemModal, setShowSystemModal] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  
  // Form states for Process Systems
  const [systemForm, setSystemForm] = useState({ name: '', description: '', type: 'client', system_url: '', order: 0, logo_url: '' });
  const [guideForm, setGuideForm] = useState({ title: '', description: '', steps: [], order: 0 });
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: '', order: 0 });
  
  // Editing states for Process Systems
  const [editingSystem, setEditingSystem] = useState(null);
  const [editingGuide, setEditingGuide] = useState(null);
  const [editingFaq, setEditingFaq] = useState(null);

  // Notification system for Dashboard
  const [notifications, setNotifications] = useState([]);
  
  // PDF Management states
  const [selectedCategoryForPdf, setSelectedCategoryForPdf] = useState(null);
  const [showPdfListModal, setShowPdfListModal] = useState(false);

  const showSuccessNotification = (message) => {
    const id = Date.now();
    const newNotification = {
      id,
      type: 'success',
      message,
      timestamp: new Date()
    };
    setNotifications(prev => [...prev, newNotification]);
    setTimeout(() => removeNotification(id), 5000);
  };

  const showErrorNotification = (message) => {
    const id = Date.now();
    const newNotification = {
      id,
      type: 'error',
      message,
      timestamp: new Date()
    };
    setNotifications(prev => [...prev, newNotification]);
    setTimeout(() => removeNotification(id), 8000);
  };

  const showInfoNotification = (message) => {
    const id = Date.now();
    const newNotification = {
      id,
      type: 'info',
      message,
      timestamp: new Date()
    };
    setNotifications(prev => [...prev, newNotification]);
    setTimeout(() => removeNotification(id), 6000);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };
  
  // PDF List Modal Handler
  const openPdfListModal = (category) => {
    setSelectedCategoryForPdf(category);
    setShowPdfListModal(true);
  };

  // Process Systems Handler Functions
  const handleCreateSystem = async () => {
    console.log('🚀 handleCreateSystem called with form data:', systemForm);
    try {
      console.log('📡 Making API call to:', `${API}/process-systems`);
      const response = await axios.post(`${API}/process-systems`, systemForm);
      console.log('✅ API response:', response.data);
      setShowSystemModal(false);
      setSystemForm({ name: '', description: '', type: 'client', system_url: '', order: 0, logo_url: '' });
      
      // Refresh systems list to show new system with logo
      if (refreshSystemDetails && refreshSystemDetails.current) {
        refreshSystemDetails.current();
      }
      
      showSuccessNotification('System erfolgreich erstellt!');
    } catch (error) {
      console.error('❌ Error creating system:', error);
      console.error('❌ Error response:', error.response?.data);
      showErrorNotification('Fehler beim Erstellen des Systems');
    }
  };

  const handleUpdateSystem = async () => {
    try {
      await axios.put(`${API}/process-systems/${editingSystem.id}`, systemForm);
      setShowSystemModal(false);
      setEditingSystem(null);
      setSystemForm({ name: '', description: '', type: 'client', system_url: '', order: 0, logo_url: '' });

      // Refresh systems list to show updated system with logo
      if (refreshSystemDetails && refreshSystemDetails.current) {
        refreshSystemDetails.current();
      }

      toast.success('System erfolgreich aktualisiert!');
    } catch (error) {
      console.error('Error updating system:', error);
      toast.error('Fehler beim Aktualisieren des Systems');
    }
  };

  const handleCreateGuide = async () => {
    try {
      const guideData = {
        ...guideForm,
        system_id: selectedProcessSystem.id
      };
      await axios.post(`${API}/process-systems/${selectedProcessSystem.id}/guides`, guideData);
      setShowGuideModal(false);
      setGuideForm({ title: '', content: '', order: 0 });
      
      // Refresh the lists to show the new guide immediately
      if (refreshSystemDetails.current) {
        refreshSystemDetails.current();
      }
      
      showSuccessNotification('Anleitung erfolgreich erstellt!');
    } catch (error) {
      console.error('Error creating guide:', error);
      showErrorNotification('Fehler beim Erstellen der Anleitung');
    }
  };

  const handleUpdateGuide = async () => {
    try {
      await axios.put(`${API}/process-guides/${editingGuide.id}`, guideForm);
      setShowGuideModal(false);
      setEditingGuide(null);
      setGuideForm({ title: '', content: '', order: 0 });
      showSuccessNotification('Anleitung erfolgreich aktualisiert!');
    } catch (error) {
      console.error('Error updating guide:', error);
      showErrorNotification('Fehler beim Aktualisieren der Anleitung');
    }
  };

  const handleCreateFaq = async () => {
    try {
      const faqData = {
        ...faqForm,
        system_id: selectedProcessSystem.id
      };
      await axios.post(`${API}/process-systems/${selectedProcessSystem.id}/faqs`, faqData);
      setShowFaqModal(false);
      setFaqForm({ question: '', answer: '', category: '', order: 0 });
      
      // Refresh the lists to show the new FAQ immediately
      if (refreshSystemDetails.current) {
        refreshSystemDetails.current();
      }
      
      showSuccessNotification('FAQ erfolgreich erstellt!');
    } catch (error) {
      console.error('Error creating FAQ:', error);
      showErrorNotification('Fehler beim Erstellen der FAQ');
    }
  };

  const handleUpdateFaq = async () => {
    try {
      await axios.put(`${API}/process-faqs/${editingFaq.id}`, faqForm);
      setShowFaqModal(false);
      setEditingFaq(null);
      setFaqForm({ question: '', answer: '', category: '', order: 0 });
      showSuccessNotification('FAQ erfolgreich aktualisiert!');
    } catch (error) {
      console.error('Error updating FAQ:', error);
      showErrorNotification('Fehler beim Aktualisieren der FAQ');
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile) {
      showErrorNotification('Bitte wählen Sie eine Datei aus');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);
    if (uploadDescription) {
      formData.append('description', uploadDescription);
    }

    try {
      setUploading(true);
      await axios.post(`${API}/process-systems/${selectedProcessSystem.id}/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setShowDocumentModal(false);
      setUploadFile(null);
      setUploadDescription('');
      
      // Refresh the lists to show the new document immediately
      if (refreshSystemDetails.current) {
        refreshSystemDetails.current();
      }
      
      showSuccessNotification('Dokument erfolgreich hochgeladen!');
    } catch (error) {
      console.error('Error uploading document:', error);
      showErrorNotification('Fehler beim Hochladen des Dokuments');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (window.confirm('Sind Sie sicher, dass Sie dieses Dokument löschen möchten?')) {
      try {
        await axios.delete(`${API}/process-documents/${documentId}`);
        showSuccessNotification('Dokument erfolgreich gelöscht!');
      } catch (error) {
        console.error('Error deleting document:', error);
        showErrorNotification('Fehler beim Löschen des Dokuments');
      }
    }
  };

  const handleDeleteSystem = async (systemId) => {
    if (window.confirm('Sind Sie sicher, dass Sie dieses System löschen möchten?')) {
      try {
        await axios.delete(`${API}/process-systems/${systemId}`);
        if (selectedProcessSystem && selectedProcessSystem.id === systemId) {
          setSelectedProcessSystem(null);
        }
        if (refreshSystemDetails && refreshSystemDetails.current) {
          refreshSystemDetails.current();
        }
        toast.success('System erfolgreich gelöscht!');
      } catch (error) {
        console.error('Error deleting system:', error);
        toast.error('Fehler beim Löschen des Systems');
      }
    }
  };

  // Refresh function to update lists after changes
  const refreshSystemDetails = React.useRef();
  
  const setRefreshFunction = (refreshFn) => {
    refreshSystemDetails.current = refreshFn;
  };

  // PDF Download Function with Authentication (for Dashboard)
  const handleDownloadCategoryPdf = async (pdfId, filename) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showErrorNotification('Nicht authentifiziert');
        return;
      }

      // Use fetch to download with authentication
      const response = await fetch(`${API}/categories/pdfs/${pdfId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      // Get the blob data
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download.pdf';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showErrorNotification('Fehler beim Herunterladen der PDF');
    }
  };

  useEffect(() => {
    fetchDashboardData().then(() => {
      // Handle pending redirect from email deep links
      const pendingRedirect = localStorage.getItem('pending_redirect');
      if (pendingRedirect) {
        try {
          const redirect = JSON.parse(pendingRedirect);
          if (redirect.type === 'category' && redirect.category_id) {
            setCurrentPage('dashboard');
            // Find the category and navigate to it
            setTimeout(async () => {
              try {
                const res = await axios.get(`${API}/categories`);
                const cat = res.data.find(c => c.id === redirect.category_id);
                if (cat) {
                  setSelectedCategory(cat);
                  fetchModules(cat.id);
                  window.history.pushState({ view: 'category', categoryId: cat.id }, '');
                }
              } catch {}
            }, 300);
          }
        } catch {}
        localStorage.removeItem('pending_redirect');
      }
    });
  }, []);

  const fetchDashboardData = async () => {
    try {
      // ── Stufe 1: Kritische Daten → UI sofort anzeigen ──────────────────────
      const [categoriesRes, dashboardRes, userRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/auth/me`).catch(() => ({ data: null })),
      ]);

      setCategories(categoriesRes.data);
      setDashboardStats(dashboardRes.data);
      setLoading(false); // UI sofort zeigen – kein langer Spinner mehr

      // ── Stufe 2: Sekundäre Daten parallel nachladen ────────────────────────
      const [badgesRes, certificateStatusRes, examBookingRes, appointmentsRes, rankRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/badges`).catch(() => ({ data: [] })),
        axios.get(`${API}/certificate/status`).catch(() => ({ data: {} })),
        axios.get(`${API}/exam/booking-url`).catch(() => ({ data: { booking_url: '', status: null } })),
        axios.get(`${API}/appointments/appointments/my`).catch(() => ({ data: [] })),
        axios.get(`${API}/user/my-rank`).catch(() => ({ data: null })),
        axios.get(`${API}/user/team-leaderboard`).catch(() => ({ data: { leaderboard: [] } })),
      ]);

      // Badges
      const newBadges = badgesRes.data;
      if (prevBadgeCount !== null && newBadges.length > prevBadgeCount) {
        const newest = newBadges[0];
        if (newest) toast.success(`${newest.icon || '🏅'} Badge verdient: ${newest.title}!`, { duration: 4000 });
      }
      setPrevBadgeCount(newBadges.length);
      setBadges(newBadges);

      setCertificateStatus(certificateStatusRes.data);
      setExamBookingUrl(examBookingRes.data.booking_url);
      const freshExamStatus = userRes.data?.academy_exam_status || examBookingRes.data.status;
      setExamStatus(freshExamStatus);
      setUserAppointments(appointmentsRes.data || []);
      if (rankRes.data) setRankData(rankRes.data);
      if (leaderboardRes.data) setLeaderboard(leaderboardRes.data.leaderboard || []);

      // ── Stufe 3: PDF-Daten parallel laden (kein N+1 mehr) ─────────────────
      const categoryPdfsData = {};
      await Promise.all(
        categoriesRes.data.map(async (category) => {
          try {
            const pdfsRes = await axios.get(`${API}/categories/${category.id}/pdfs`);
            categoryPdfsData[category.id] = pdfsRes.data;
          } catch { categoryPdfsData[category.id] = []; }
        })
      );
      setCategoryPdfs(categoryPdfsData);

      // Zertifikate nur laden wenn abgeschlossen
      if (certificateStatusRes.data.completed) {
        try {
          const certificatesRes = await axios.get(`${API}/certificate/list`);
          setCertificates(certificatesRes.data);
        } catch {}
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false); // Sicherheitsnetz falls Stufe 1 fehlschlägt
    }
  };
  
  // Handle manual exam booking (fallback if webhook fails)
  const handleBookExamManually = async () => {
    try {
      await axios.post(`${API}/exam/book-manually`);
      setExamStatus('exam_booked');
      toast.success('Termin wurde als gebucht markiert');
    } catch (error) {
      console.error('Error booking exam manually:', error);
    }
  };
  
  // Generate certificate function for vertriebler
  const handleCertificateGeneration = async () => {
    setCertificateLoading(true);
    try {
      const response = await axios.get(`${API}/certificate/generate`);
      
      if (response.data.success) {
        console.log('🏆 Certificate generated successfully!');
        // Update certificate status
        setCertificateStatus({ ...certificateStatus, completed: true });
        
        // Refresh certificates list
        const certificatesRes = await axios.get(`${API}/certificate/list`);
        setCertificates(certificatesRes.data);
        
        // Show certificate modal
        setShowCertificate(true);
        
        // Automatically download the certificate
        if (certificatesRes.data.length > 0) {
          await downloadCertificate(certificatesRes.data[0].id);
        }
      }
    } catch (error) {
      console.error('Error generating certificate:', error);
      showErrorNotification('Fehler beim Generieren des Zertifikats: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCertificateLoading(false);
    }
  };
  
  // Download certificate function
  const downloadCertificate = async (certificateId) => {
    try {
      const response = await axios.get(`${API}/certificate/download/${certificateId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Zertifikat_${user.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log('📥 Certificate downloaded successfully');
    } catch (error) {
      console.error('Error downloading certificate:', error);
      showErrorNotification('Fehler beim Herunterladen des Zertifikats');
    }
  };

  const fetchModules = async (categoryId) => {
    try {
      // Use timestamp to prevent caching
      const timestamp = Date.now();
      const response = await axios.get(`${API}/modules?category_id=${categoryId}&_t=${timestamp}`);
      
      console.log('🔍 API Response for modules:', response.data);
      console.log('🔍 Response type:', Array.isArray(response.data) ? 'Array' : typeof response.data);
      
      // Handle different response formats
      const moduleData = response.data.modules || response.data || [];
      console.log('📋 Setting modules:', moduleData.length, 'modules found');
      
      setModules(Array.isArray(moduleData) ? moduleData : []);
    } catch (error) {
      console.error('❌ Error fetching modules:', error);
      setModules([]); // Ensure modules is always an array
    }
  };

  const handleCategorySelect = (category) => {
    console.log('🎯 CATEGORY SELECTED:', category.name, 'ID:', category.id);
    
    // Only allow selection of unlocked categories
    if (user.role === 'vertriebler' && !category.is_unlocked) {
      return; // Do nothing for locked categories
    }
    
    setSelectedCategory(category);
    fetchModules(category.id);
    // Push history state so mobile back button returns to category list
    window.history.pushState({ view: 'category', categoryId: category.id }, '');
  };

  const checkForUnlockedCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories/unlock-check`);
      const unlocked = response.data.newly_unlocked;
      
      if (unlocked.length > 0) {
        setNewlyUnlockedCategories(unlocked);
        setShowUnlockNotification(true);
        
        // Auto-hide notification after 5 seconds
        setTimeout(() => {
          setShowUnlockNotification(false);
        }, 5000);
      }
      
      // Also refresh modules if a category is selected
      if (selectedCategory) {
        fetchModules(selectedCategory.id);
      }
    } catch (error) {
      console.error('Error checking unlock status:', error);
    }
  };

  const handleModuleStart = async (module) => {
    try {
      if (isReadOnly) {
        // Read-only mode: just open viewer, no progress tracking
        setSelectedModule(module);
        setShowModuleViewer(true);
        return;
      }
      // Only create progress entry for modules that aren't already completed
      if (module.completion_status !== 'completed') {
        await axios.post(`${API}/progress`, {
          module_id: module.id,
          completed: false
        });
      }
      
      // Open module viewer
      setSelectedModule(module);
      setShowModuleViewer(true);
      
      // Only refresh data if progress was actually changed
      if (module.completion_status !== 'completed') {
        fetchDashboardData();
        fetchModules(selectedCategory.id);
      }
    } catch (error) {
      console.error('Error starting module:', error);
    }
  };

  const handleModuleComplete = async (module, moduleQuizAnswers = {}) => {
    try {
      console.log('🚀 STARTING MODULE COMPLETION - Module:', module.title);
      console.log('🔍 Current categories state before completion:', categories);
      
      // Complete the module on backend
      const progressData = {
        module_id: module.id,
        completed: true,
        score: 100
      };
      
      // Add quiz answers if it's a quiz module
      if (module.content_type === 'quiz' && Object.keys(moduleQuizAnswers).length > 0) {
        // Convert quiz answers to the expected format: List[List[int]]
        const quizAnswersArray = [];
        const quizData = { questions: getQuizQuestions(module) };
        
        if (quizData.questions) {
          quizData.questions.forEach((question, index) => {
            const userAnswer = moduleQuizAnswers[index];
            if (userAnswer !== undefined) {
              // Ensure answer is always an array (for consistency with multiple choice)
              quizAnswersArray[index] = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
            } else {
              quizAnswersArray[index] = []; // No answer provided
            }
          });
        }
        
        progressData.quiz_answers = quizAnswersArray;
        console.log('📝 Submitting quiz answers:', quizAnswersArray);
      }
      
      const progressResponse = await axios.post(`${API}/progress`, progressData);
      
      console.log('✅ Module completion API response:', progressResponse.data);
      
      // Server is authoritative: if it did not mark the module completed, do not pretend it did
      if (progressResponse.data?.completed === false) {
        console.warn('⚠️ Server did not mark module as completed');
        toast.error('Das Quiz wurde vom Server nicht als bestanden gewertet. Bitte versuche es erneut.');
        return false;
      }
      
      // IMMEDIATE STATE UPDATE - Update local categories state immediately
      console.log('🔄 Updating categories state immediately...');
      
      const updatedCategories = categories.map(category => {
        if (category.id === module.category_id) {
          const newCompleted = category.completed_modules + 1;
          const newPercentage = Math.round((newCompleted / category.modules_count) * 100);
          
          console.log(`📊 Category ${category.name}: ${category.completed_modules}/${category.modules_count} → ${newCompleted}/${category.modules_count} (${newPercentage}%)`);
          
          return {
            ...category,
            completed_modules: newCompleted,
            completion_percentage: newPercentage
          };
        }
        return category;
      });
      
      // Force immediate UI update for categories
      setCategories(updatedCategories);
      console.log('⚡ Categories state updated immediately');
      
      // CRITICAL: Also update selectedCategory if user is viewing that category
      if (selectedCategory && selectedCategory.id === module.category_id) {
        const updatedSelectedCategory = {
          ...selectedCategory,
          completed_modules: selectedCategory.completed_modules + 1,
          completion_percentage: Math.round(((selectedCategory.completed_modules + 1) / selectedCategory.modules_count) * 100)
        };
        setSelectedCategory(updatedSelectedCategory);
        console.log('⚡ Selected category progress updated immediately:', updatedSelectedCategory);
      }
      
      // Also update dashboard stats immediately
      if (dashboardStats) {
        const newCompletedModules = dashboardStats.completed_modules + 1;
        const newCompletionPercentage = (newCompletedModules / dashboardStats.total_modules) * 100;
        
        // For now, remove next_module if we've completed all modules
        const updatedDashboardStats = {
          ...dashboardStats,
          completed_modules: newCompletedModules,
          completion_percentage: newCompletionPercentage
        };
        
        // If we've completed all modules, remove the next_module 
        if (newCompletedModules >= dashboardStats.total_modules) {
          updatedDashboardStats.next_module = null;
          console.log('🎉 All modules completed - removing next_module from dashboard');
        }
        
        setDashboardStats(updatedDashboardStats);
        console.log('📊 Dashboard stats updated immediately');
      }
      
      // IMMEDIATE Dashboard refresh to get correct next_module
      setTimeout(async () => {
        try {
          console.log('⚡ IMMEDIATE Dashboard refresh to fix next_module...');
          const dashboardRes = await axios.get(`${API}/dashboard/stats?_t=${Date.now()}`);
          setDashboardStats(dashboardRes.data);
          console.log('✅ Dashboard next_module updated immediately');
        } catch (error) {
          console.error('❌ Immediate dashboard refresh failed:', error);
        }
      }, 500); // Very quick refresh
      
      // Background sync to ensure data consistency
      setTimeout(async () => {
        try {
          console.log('🔄 Background sync: Fetching fresh data from server...');
          const timestamp = Date.now();
          
          // Update both categories AND dashboard stats to fix "Nächstes Modul" issue
          const [categoriesRes, dashboardRes] = await Promise.all([
            axios.get(`${API}/categories?_t=${timestamp}`),
            axios.get(`${API}/dashboard/stats?_t=${timestamp}`)
          ]);
          
          console.log('📊 Fresh categories data from server:', categoriesRes.data);
          console.log('📊 Fresh dashboard data from server:', dashboardRes.data);
          
          setCategories(categoriesRes.data);
          setDashboardStats(dashboardRes.data);
          
          console.log('✅ Background sync completed - Dashboard and categories updated');
        } catch (error) {
          console.error('❌ Background sync failed:', error);
        }
      }, 1000);
      
      // Refresh module list if in category view
      if (selectedCategory?.id) {
        console.log('🔄 Refreshing modules for category:', selectedCategory.name);
        
        // IMMEDIATE: Update the completed module in local state first
        const updatedModules = modules.map(mod => {
          if (mod.id === module.id) {
            return { ...mod, completion_status: 'completed' };
          }
          return mod;
        });
        setModules(updatedModules);
        console.log('⚡ Module completion status updated immediately in local state');
        
        // Then fetch fresh data from server
        await fetchModules(selectedCategory.id);
        // Also refresh exam status
        fetchDashboardData();
      }
      
      // Check for unlocked categories for sales reps
      if (user.role === 'vertriebler') {
        setTimeout(() => {
          console.log('🔓 Checking for unlocked categories...');
          checkForUnlockedCategories();
        }, 1500);
      }
      
      // Close module viewer
      setSelectedModule(null);
      setShowModuleViewer(false);
      
      console.log('🎉 Module completion process finished - UI should be updated immediately!');
      return true;
    } catch (error) {
      console.error('❌ Error completing module:', error);
      toast.error('Fortschritt konnte nicht gespeichert werden. Bitte versuche es erneut.');
      return false;
    }
  };

  const generateCertificate = async () => {
    try {
      await axios.post(`${API}/certificates`);
      fetchDashboardData();
    } catch (error) {
      console.error('Error generating certificate:', error);
      showInfoNotification('Alle Module müssen abgeschlossen sein, um ein Zertifikat zu generieren.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {showHeader && <Header />}
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {showHeader && <Header />}
      
      {/* Navigation Tabs - Only show if showNavigation is true */}
      {showNavigation && (
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="container mx-auto px-4">
            <nav className="flex space-x-8">
              <button
                onClick={() => {
                  handlePageChange('dashboard');
                  setSelectedCategory(null);
                  setSelectedProcessSystem(null);
                }}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${
                  currentPage === 'dashboard'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Dashboard
              </button>
              {['vertriebler', 'teamleiter'].includes(user?.role) && (
                <button
                  onClick={() => handlePageChange('erfolge')}
                  className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center gap-1.5 ${
                    currentPage === 'erfolge'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  data-testid="erfolge-tab"
                >
                  Erfolge
                  {rankData && user?.role === 'vertriebler' && (
                    <span className="text-base">{rankData.rank?.icon}</span>
                  )}
                </button>
              )}
            </nav>
          </div>
        </div>
      )}
      
      <div className="container mx-auto px-4 py-8">
        {/* Render current page content */}
        {currentPage === 'dashboard' && (
          <>
            {/* Back Button */}
            {selectedCategory && (
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  fetchDashboardData();
                  // Go back in history to match the popstate handler
                  if (window.history.state?.view === 'category') {
                    window.history.back();
                  }
                }}
                className="mb-6 flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                data-testid="back-to-categories-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Zurück zu den Kategorien</span>
              </button>
            )}

            {!selectedCategory ? (
              <>
                {/* Dashboard Header */}
                <div className="bg-white rounded-xl p-6 mb-6 border border-slate-100" style={{boxShadow: 'var(--shadow-card)'}}>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-0.5">Dashboard</h1>
              <p className="text-slate-500 text-sm">Willkommen zurück, {user.name}!</p>
              
              {/* Progress Overview */}
              {dashboardStats && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="stat-card card-enter bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-2xl font-bold text-blue-600">{dashboardStats.completed_modules}</div>
                        <div className="text-xs text-slate-500 mt-0.5">von {dashboardStats.total_modules} Modulen</div>
                      </div>
                      <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                      </div>
                    </div>
                    <div className="text-xs font-medium text-slate-600 mt-2">Module</div>
                  </div>
                  <div className="stat-card card-enter bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-2xl font-bold text-emerald-600">{dashboardStats.completed_categories}</div>
                        <div className="text-xs text-slate-500 mt-0.5">von {dashboardStats.total_categories} Kategorien</div>
                      </div>
                      <div className="p-2 bg-emerald-50 rounded-lg flex-shrink-0">
                        <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                    </div>
                    <div className="text-xs font-medium text-slate-600 mt-2">Kategorien</div>
                  </div>
                  <div className="stat-card card-enter bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-2xl font-bold text-amber-600">{dashboardStats.completion_percentage.toFixed(0)}%</div>
                        <div className="text-xs text-slate-500 mt-0.5">Gesamtfortschritt</div>
                      </div>
                      <div className="p-2 bg-amber-50 rounded-lg flex-shrink-0">
                        <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                    </div>
                    <div className="text-xs font-medium text-slate-600 mt-2">Fortschritt</div>
                  </div>
                  {rankData && (
                    <button
                      onClick={() => handlePageChange('erfolge')}
                      className="stat-card card-enter bg-white rounded-xl p-4 shadow-sm flex flex-col items-start text-left"
                      style={{borderLeft: `3px solid ${rankData.rank?.color || '#6B7280'}`}}
                      data-testid="rank-chip-main-dashboard"
                    >
                      <div className="flex items-start justify-between w-full">
                        <div>
                          <div className="text-sm font-bold text-gray-800">{rankData.rank?.name || 'Einsteiger'}</div>
                          <div className="text-xs text-gray-400 mt-0.5">Dein Rang</div>
                        </div>
                        <div className="p-2 rounded-lg flex-shrink-0" style={{backgroundColor: (rankData.rank?.color || '#6B7280') + '18'}}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" style={{color: rankData.rank?.color || '#6B7280'}}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                        </div>
                      </div>
                      <div className="text-xs text-blue-500 mt-2 font-medium">Erfolge ansehen →</div>
                    </button>
                  )}
                </div>
              )}
              
              {/* Progress Bar */}
              {dashboardStats && (
                <div className="mt-6">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Gesamtfortschritt</span>
                    <span>{dashboardStats.completed_modules}/{dashboardStats.total_modules} Module</span>
                  </div>
                  <ProgressBar 
                    current={dashboardStats.completed_modules} 
                    total={dashboardStats.total_modules}
                  />
                </div>
              )}
            </div>

            {/* ── RANG & BADGES SECTION ── */}
            {/* Moved to "Erfolge" tab - only show compact rank chip in stats */}

            {/* Next Module */}
            {dashboardStats?.next_module && (
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6 mb-8">
                <h2 className="text-xl font-semibold text-blue-900 mb-2">Nächstes Modul</h2>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-blue-800">{dashboardStats.next_module.title}</h3>
                    <p className="text-blue-600">{dashboardStats.next_module.description}</p>
                  </div>
                  <button
                    onClick={() => {
                      // Find the category for this module
                      const moduleCategory = categories.find(cat => cat.id === dashboardStats.next_module.category_id);
                      if (moduleCategory) {
                        handleCategorySelect(moduleCategory);
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                  >
                    Fortsetzen
                  </button>
                </div>
              </div>
            )}

            {/* Categories - Split into Mandatory and View-Only */}
            <div className="mb-8">
              {(() => {
                const mandatoryCategories = categories.filter(cat => cat.is_mandatory !== false);
                const viewOnlyCategories = categories.filter(cat => cat.is_mandatory === false);
                
                return (
                  <>
                    {/* Mandatory Categories */}
                    {mandatoryCategories.length > 0 && (
                      <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">
                          📚 Pflicht-Schulungen
                        </h2>
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                          {mandatoryCategories.map((category) => (
                            <CategoryCard
                              key={category.id}
                              category={category}
                              onSelect={handleCategorySelect}
                              readOnly={isReadOnly}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* View-Only Categories */}
                    {viewOnlyCategories.length > 0 && (
                      <div>
                        <div className="flex items-center space-x-3 mb-6">
                          <h2 className="text-2xl font-bold text-blue-800">
                            👁️ Zur Einsicht
                          </h2>
                          <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                            Optional - Nicht erforderlich für Abschluss
                          </span>
                        </div>
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                          {viewOnlyCategories.map((category) => (
                            <CategoryCard
                              key={category.id}
                              category={category}
                              onSelect={handleCategorySelect}
                              readOnly={isReadOnly}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Academy Exam Status Banner - Only for Vertriebler */}
            {user?.role === 'vertriebler' && examStatus && examStatus !== 'exam_passed' && (
              <ExamStatusBanner 
                examStatus={examStatus}
                onBookAppointment={() => handlePageChange('appointments')}
                retryCategories={user?.academy_exam_retry_categories || []}
                categories={categories}
                appointments={userAppointments}
                cancellationCount={user?.appointment_cancellation_count || 0}
                onStatusChange={async () => {
                  await refreshUser();
                  fetchDashboardData();
                  handlePageChange('dashboard');
                }}
              />
            )}
            
            {/* Certificate Generation */}
            {certificateStatus?.completed && examStatus === 'exam_passed' && certificates.length === 0 && (
              <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl p-6 mb-8">
                <h3 className="text-xl font-semibold text-green-800 mb-2">
                  🎉 Herzlichen Glückwunsch!
                </h3>
                <p className="text-green-700 mb-4">
                  Sie haben alle Schulungsmodule erfolgreich abgeschlossen. Generieren Sie jetzt Ihr Zertifikat.
                </p>
                <button
                  onClick={handleCertificateGeneration}
                  disabled={certificateLoading}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {certificateLoading ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Wird generiert...
                    </div>
                  ) : (
                    'Zertifikat generieren'
                  )}
                </button>
              </div>
            )}
            
            {/* Show Certificate Downloads if user has certificates */}
            {certificates.length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6 mb-8">
                <h3 className="text-xl font-semibold text-blue-800 mb-2">
                  🏆 Ihre Zertifikate
                </h3>
                <p className="text-blue-700 mb-4">
                  Sie haben {certificates.length} Zertifikat(e) erhalten.
                </p>
                <div className="space-y-2">
                  {certificates.map((cert, index) => (
                    <div key={cert.id} className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            Zertifikat #{index + 1}
                          </p>
                          <p className="text-sm text-gray-600">
                            Ausgestellt am: {cert.issued_date || 'Heute'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => downloadCertificate(cert.id)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>Herunterladen</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          /* Selected Category View */
          <div>
            <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
              <div className="flex items-center space-x-4">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl"
                  style={{ backgroundColor: selectedCategory.color }}
                >
                  {selectedCategory.icon || selectedCategory.title[0]}
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{selectedCategory.title}</h1>
                  <p className="text-gray-600">{selectedCategory.description}</p>
                </div>
              </div>
              
              <div className="mt-6">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>{isReadOnly ? 'Module in dieser Kategorie' : 'Fortschritt in dieser Kategorie'}</span>
                  <span>{isReadOnly ? `${selectedCategory.modules_count} Module` : `${selectedCategory.completed_modules}/${selectedCategory.modules_count} Module`}</span>
                </div>
                {isReadOnly ? (
                  <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>Nur Einsicht – kein Fortschritt</span>
                  </div>
                ) : (
                  <ProgressBar 
                    current={selectedCategory.completed_modules} 
                    total={selectedCategory.modules_count}
                    color={selectedCategory.color?.replace('#', '') || 'blue'}
                  />
                )}
              </div>
            </div>

            {/* Modules */}
            <div className="space-y-6">
              {/* DEBUG: Check modules state during rendering */}
              {console.log('🎯 RENDERING MODULES:', modules.length, 'modules in state:', modules)}
              
              {modules.map((module) => (
                <ModuleCard
                  key={module.id}
                  module={module}
                  onStart={handleModuleStart}
                  onComplete={isReadOnly ? null : handleModuleComplete}
                />
              ))}
            </div>

            {/* Category PDF Materials Section - After Modules with Spacing */}
            {categoryPdfs[selectedCategory.id]?.length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-8 mt-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-blue-900">📚 Zusätzliche Materialien</h3>
                    <p className="text-blue-700">Wichtige Dokumente und Leitfäden für diese Kategorie</p>
                  </div>
                </div>
                
                <div className="grid gap-4 md:grid-cols-2">
                  {categoryPdfs[selectedCategory.id].map((pdf) => (
                    <div key={pdf.id} className="bg-white border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="w-8 h-8 bg-red-500 rounded flex items-center justify-center">
                              <span className="text-white text-xs font-bold">PDF</span>
                            </div>
                            <h4 className="font-semibold text-gray-900 truncate">{pdf.filename}</h4>
                          </div>
                          
                          {pdf.description && (
                            <p className="text-sm text-gray-600 mb-3">{pdf.description}</p>
                          )}
                          
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>📅 {new Date(pdf.upload_date).toLocaleDateString('de-DE')}</span>
                            <span>💾 {Math.round(pdf.file_size / 1024)} KB</span>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => handleDownloadCategoryPdf(pdf.id, pdf.original_filename)}
                          className="ml-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 shadow-md hover:shadow-lg"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span>Download</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {modules.length === 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Noch keine Module verfügbar
                </h3>
                <p className="text-gray-600">
                  In dieser Kategorie sind noch keine Module verfügbar.
                </p>
              </div>
            )}

            {/* "Zurück zur Übersicht" Button - shown when all modules are completed */}
            {!isReadOnly && selectedCategory.completed_modules >= selectedCategory.modules_count && selectedCategory.modules_count > 0 && (
              <div className="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-green-800">Stark! Alle Module abgeschlossen.</h3>
                  <p className="text-green-700 text-sm">Diese Kategorie ist erledigt – weiter geht's!</p>
                  <button
                    onClick={() => {
                      setSelectedCategory(null);
                      fetchDashboardData();
                      if (window.history.state?.view === 'category') {
                        window.history.back();
                      }
                    }}
                    className="mt-2 bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-semibold transition-colors flex items-center space-x-2 shadow-md hover:shadow-lg"
                    data-testid="category-done-btn"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    <span>Zurück zur Übersicht</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Module Viewer */}
        <ModuleViewer
          module={selectedModule}
          isOpen={showModuleViewer}
          onClose={() => {
            setShowModuleViewer(false);
            setSelectedModule(null);
          }}
          onComplete={isReadOnly ? null : handleModuleComplete}
        />
        
        {/* Modern Certificate Modal */}
        {showCertificate && certificates.length > 0 && (
          <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="no-print flex justify-between items-center p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-800">🏆 Ihr Zertifikat</h2>
                <button
                  onClick={() => setShowCertificate(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Certificate Display */}
              <div className="p-8">
                <ModernCertificate 
                  user={user}
                  completedCategories={categories.filter(cat => cat.completed_modules === cat.modules_count).map(cat => cat.title)}
                  certificateId={certificates[0]?.id}
                  issuedDate={certificates[0]?.created_at}
                />
              </div>
              
              {/* Action Buttons */}
              <div className="no-print flex justify-center space-x-4 p-6 bg-gray-50 rounded-b-3xl">
                <button
                  onClick={() => window.print()}
                  className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-medium transition-colors flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  <span>Drucken</span>
                </button>
                <button
                  onClick={() => {
                    if (certificates.length > 0) {
                      downloadCertificate(certificates[0].id);
                    }
                  }}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>PDF herunterladen</span>
                </button>
                <button
                  onClick={() => {
                    // Share functionality - could open social media sharing
                    if (navigator.share) {
                      navigator.share({
                        title: 'Mein unfold sales Zertifikat',
                        text: `Ich habe erfolgreich alle Module der unfold sales Akademie abgeschlossen! 🏆`,
                        url: window.location.href
                      });
                    } else {
                      // Fallback: copy to clipboard
                      navigator.clipboard.writeText(`Ich habe erfolgreich alle Module der unfold sales Akademie abgeschlossen! 🏆 ${window.location.href}`);
                      toast.success('Text wurde in die Zwischenablage kopiert!');
                    }
                  }}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  <span>Teilen</span>
                </button>
              </div>
            </div>
          </div>
        )}
        </>
        )}
        
        {currentPage === 'processes' && (
          <ProcessSystemsView 
            user={user} 
            selectedSystem={selectedProcessSystem} 
            setSelectedSystem={(system) => {
              setSelectedProcessSystem(system);
              if (system) {
                window.history.pushState({ view: 'processSystem', systemId: system.id }, '');
              }
            }}
            // Modal states - Sales partners get read-only access
            showSystemModal={user.role === 'vertriebler' ? false : showSystemModal}
            setShowSystemModal={user.role === 'vertriebler' ? () => {} : setShowSystemModal}
            showGuideModal={user.role === 'vertriebler' ? false : showGuideModal}
            setShowGuideModal={user.role === 'vertriebler' ? () => {} : setShowGuideModal}
            showFaqModal={user.role === 'vertriebler' ? false : showFaqModal}
            setShowFaqModal={user.role === 'vertriebler' ? () => {} : setShowFaqModal}
            showDocumentModal={user.role === 'vertriebler' ? false : showDocumentModal}
            setShowDocumentModal={user.role === 'vertriebler' ? () => {} : setShowDocumentModal}
            // Form states - Sales partners don't need forms
            systemForm={user.role === 'vertriebler' ? null : systemForm}
            setSystemForm={user.role === 'vertriebler' ? () => {} : setSystemForm}
            guideForm={user.role === 'vertriebler' ? null : guideForm}
            setGuideForm={user.role === 'vertriebler' ? () => {} : setGuideForm}
            faqForm={user.role === 'vertriebler' ? null : faqForm}
            setFaqForm={user.role === 'vertriebler' ? () => {} : setFaqForm}
            // Editing states - Sales partners don't edit
            editingSystem={user.role === 'vertriebler' ? null : editingSystem}
            setEditingSystem={user.role === 'vertriebler' ? () => {} : setEditingSystem}
            editingGuide={user.role === 'vertriebler' ? null : editingGuide}
            setEditingGuide={user.role === 'vertriebler' ? () => {} : setEditingGuide}
            editingFaq={user.role === 'vertriebler' ? null : editingFaq}
            setEditingFaq={user.role === 'vertriebler' ? () => {} : setEditingFaq}
            // Upload states - Sales partners don't upload
            uploadFile={user.role === 'vertriebler' ? null : uploadFile}
            setUploadFile={user.role === 'vertriebler' ? () => {} : setUploadFile}
            uploadDescription={user.role === 'vertriebler' ? '' : uploadDescription}
            setUploadDescription={user.role === 'vertriebler' ? () => {} : setUploadDescription}
            uploading={user.role === 'vertriebler' ? false : uploading}
            setUploading={user.role === 'vertriebler' ? () => {} : setUploading}
            // Handler functions - Sales partners don't need handlers
            handleCreateSystem={user.role === 'vertriebler' ? () => {} : handleCreateSystem}
            handleUpdateSystem={user.role === 'vertriebler' ? () => {} : handleUpdateSystem}
            handleCreateGuide={user.role === 'vertriebler' ? () => {} : handleCreateGuide}
            handleUpdateGuide={user.role === 'vertriebler' ? () => {} : handleUpdateGuide}
            handleCreateFaq={user.role === 'vertriebler' ? () => {} : handleCreateFaq}
            handleUpdateFaq={user.role === 'vertriebler' ? () => {} : handleUpdateFaq}
            handleUploadDocument={user.role === 'vertriebler' ? () => {} : handleUploadDocument}
            handleDeleteDocument={user.role === 'vertriebler' ? () => {} : handleDeleteDocument}
            handleDeleteSystem={user.role === 'vertriebler' ? () => {} : handleDeleteSystem}
            // Refresh function to update lists after changes
            refreshSystemDetails={setRefreshFunction}
          />
        )}
        
        {currentPage === 'appointments' && (
          <div className="space-y-6">
            {/* Show Appointment Booking for Vertriebler if ready for exam */}
            {user?.role === 'vertriebler' && examStatus === 'ready_for_exam' && !showMyAppointments && (
              <>
                <AppointmentBooking 
                  currentUserId={user.id}
                  currentUserName={user.name}
                  onBookingComplete={() => {
                    fetchDashboardData();
                    handlePageChange('dashboard');
                  }}
                />
                <div className="text-center">
                  <button
                    onClick={() => setShowMyAppointments(true)}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Meine gebuchten Termine ansehen
                  </button>
                </div>
              </>
            )}
            
            {/* Show Appointment List */}
            {(showMyAppointments || examStatus !== 'ready_for_exam' || user?.role !== 'vertriebler') && (
              <>
                {user?.role === 'vertriebler' && examStatus === 'ready_for_exam' && (
                  <div className="mb-4">
                    <button
                      onClick={() => setShowMyAppointments(false)}
                      className="text-blue-600 hover:text-blue-800 underline flex items-center"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      Zurück zur Terminbuchung
                    </button>
                  </div>
                )}
                <AppointmentList currentUser={user} />
              </>
            )}
          </div>
        )}

        {/* ── ERFOLGE TAB ── */}
        {currentPage === 'erfolge' && (
          <div className="space-y-6">
            {/* Rang Card - Only for Vertriebler & Teamleiter */}
            {(user?.role === 'vertriebler' || user?.role === 'teamleiter') && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 flex flex-col gap-3"
                style={{ borderColor: rankData?.rank?.color || '#6B7280' }}>
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Dein Rang</h2>
                  <span className="text-3xl">{rankData?.rank?.icon || '🌱'}</span>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{rankData?.rank?.name || 'Einsteiger'}</p>
                  <p className="text-sm text-gray-400 mt-0.5">{rankData?.badge_count || 0} Badge{(rankData?.badge_count || 0) !== 1 ? 's' : ''} verdient</p>
                </div>
                {(() => {
                  const levels = [
                    { name: 'Einsteiger', min: 0, max: 2, color: '#92400E', nextIcon: '🔥', nextName: 'Azubi' },
                    { name: 'Azubi',      min: 2, max: 4, color: '#6B7280', nextIcon: '🏆', nextName: 'Profi' },
                    { name: 'Profi',      min: 4, max: 6, color: '#F59E0B', nextIcon: '💎', nextName: 'Legende' },
                    { name: 'Legende',    min: 6, max: 6, color: '#6366F1', nextIcon: null,  nextName: null },
                  ];
                  const lvl = rankData?.rank?.level || 1;
                  const level = levels[lvl - 1] || levels[0];
                  const count = rankData?.badge_count || 0;
                  const pct = level.max > level.min
                    ? Math.min(100, ((count - level.min) / (level.max - level.min)) * 100) : 100;
                  return (
                    <div>
                      <div className="w-full bg-gray-100 rounded-full h-2.5">
                        <div className="h-2.5 rounded-full transition-all duration-700"
                          style={{ width: `${pct}%`, backgroundColor: level.color }} />
                      </div>
                      {level.nextName ? (
                        <p className="text-xs text-gray-400 mt-1.5">
                          Noch {level.max - count} Badge{level.max - count !== 1 ? 's' : ''} bis {level.nextIcon} {level.nextName}
                        </p>
                      ) : (
                        <p className="text-xs text-indigo-500 mt-1.5 font-medium">Höchste Stufe erreicht!</p>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="md:col-span-2 grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl shadow-lg p-5 flex flex-col justify-between">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Abgeschlossene Module</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{dashboardStats?.completed_modules ?? 0}</p>
                  <p className="text-xs text-gray-400">von {dashboardStats?.total_modules ?? 0} Pflichtmodulen</p>
                </div>
                <div className="rounded-xl shadow-lg p-5 flex flex-col justify-between overflow-hidden relative"
                  style={{
                    background: user?.role === 'teamleiter'
                      ? (dashboardStats?.total_modules > 0 && dashboardStats?.completed_modules >= dashboardStats?.total_modules
                        ? 'linear-gradient(135deg, #064e3b 0%, #065f46 100%)'  /* grün: alle fertig */
                        : dashboardStats?.total_modules > 0
                        ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'  /* dunkel: in Ausbildung */
                        : 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)') /* blau: noch keine Module */
                      : examStatus === 'exam_passed'
                      ? 'linear-gradient(135deg, #064e3b 0%, #065f46 100%)'
                      : examStatus === 'exam_booked'
                      ? 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)'
                      : examStatus === 'ready_for_exam'
                      ? 'linear-gradient(135deg, #78350f 0%, #b45309 100%)'
                      : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                  }}>
                  {/* Background pattern */}
                  <div className="absolute inset-0 opacity-10"
                    style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                  <p className="text-xs font-semibold uppercase tracking-widest relative z-10"
                    style={{ color: 'rgba(255,255,255,0.6)' }}>Akademie-Status</p>
                  <div className="relative z-10 mt-2">
                    {examStatus === 'exam_passed' ? (
                      <>
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                          </svg>
                          <span className="text-white font-bold text-lg">Bestanden</span>
                        </div>
                        <p className="text-xs font-medium" style={{ color: '#6ee7b7' }}>Zertifiziert</p>
                      </>
                    ) : examStatus === 'exam_booked' ? (
                      <>
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                          <span className="text-white font-bold text-lg">Termin gebucht</span>
                        </div>
                        <p className="text-xs font-medium" style={{ color: '#93c5fd' }}>Abschlussgespräch</p>
                      </>
                    ) : examStatus === 'ready_for_exam' ? (
                      <>
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="#fcd34d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                          </svg>
                          <span className="text-white font-bold text-lg">Bereit</span>
                        </div>
                        <p className="text-xs font-medium" style={{ color: '#fcd34d' }}>Jetzt Termin buchen</p>
                      </>
                    ) : user?.role === 'teamleiter' ? (
                      <>
                        {dashboardStats?.total_modules > 0 && dashboardStats?.completed_modules >= dashboardStats?.total_modules ? (
                          /* Alle Teamleiter-Schulungen abgeschlossen */
                          <>
                            <div className="flex items-center gap-2 mb-1">
                              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                              </svg>
                              <span className="text-white font-bold text-lg">Abgeschlossen</span>
                            </div>
                            <p className="text-xs font-medium" style={{ color: '#6ee7b7' }}>Alle Schulungen fertig</p>
                          </>
                        ) : dashboardStats?.total_modules > 0 ? (
                          /* Noch nicht alle Schulungen fertig */
                          <>
                            <div className="flex items-center gap-2 mb-1">
                              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                              </svg>
                              <span className="text-white font-bold text-lg">In Schulung</span>
                            </div>
                            <p className="text-xs font-medium" style={{ color: '#94a3b8' }}>
                              {dashboardStats?.completed_modules || 0} von {dashboardStats?.total_modules} abgeschlossen
                            </p>
                          </>
                        ) : (
                          /* Noch keine Kategorien eingepflegt */
                          <>
                            <div className="flex items-center gap-2 mb-1">
                              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                              </svg>
                              <span className="text-white font-bold text-lg">Teamleiter</span>
                            </div>
                            <p className="text-xs font-medium" style={{ color: '#93c5fd' }}>Aktiv im Team</p>
                          </>
                        )}
                      </>

                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                          </svg>
                          <span className="text-white font-bold text-lg">In Ausbildung</span>
                        </div>
                        <p className="text-xs font-medium" style={{ color: '#94a3b8' }}>Schulungen laufen</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Badge Gallery */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-base font-bold text-gray-900 mb-4">Deine Badges</h2>
              {badges.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <span className="text-5xl mb-3">🎯</span>
                  <p className="text-sm font-medium text-gray-500">Noch keine Badges</p>
                  <p className="text-xs text-gray-400 mt-1">Schließ dein erstes Modul ab um loszulegen!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {badges.map(badge => (
                    <div key={badge.id}
                      className="flex items-center gap-3 rounded-xl px-4 py-3 border transition-shadow hover:shadow-md"
                      style={{ borderColor: (badge.color || '#3B82F6') + '50', backgroundColor: (badge.color || '#3B82F6') + '0D' }}>
                      <span className="text-2xl flex-shrink-0">{badge.icon || '🏅'}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{badge.title}</p>
                        <p className="text-xs text-gray-400">{badge.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Rangliste */}
            {leaderboard.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-base font-bold text-gray-900 mb-4">unfold's Rangliste</h2>
                <div className="space-y-2">
                  {leaderboard.slice(0, 5).map((entry, idx) => (
                    <div key={entry.id}
                      className={`flex items-center gap-3 rounded-xl px-4 py-3 transition-colors ${
                        entry.id === user?.id ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      data-testid={`leaderboard-entry-${entry.id}`}>
                      <span className="text-lg w-7 text-center flex-shrink-0 font-semibold text-gray-500">
                        {idx === 0 ? (entry.rank?.icon || '💎') : `${idx + 1}.`}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {entry.name} {entry.id === user?.id && <span className="text-xs text-blue-500 font-normal">(Du)</span>}
                        </p>
                        <p className="text-xs text-gray-400">{entry.rank?.name} · {entry.badge_count} Badges · {entry.completed_modules} Module</p>
                      </div>
                      {entry.speed_badge && (
                        <span className="text-xs bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 rounded-full flex-shrink-0">
                          ⚡ {entry.speed_badge}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Unlock Notification */}
      {showUnlockNotification && newlyUnlockedCategories.length > 0 && (
        <div className="fixed top-4 right-4 z-50 animate-bounce">
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl shadow-2xl p-6 max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="animate-pulse">🎉</div>
                <h3 className="text-lg font-bold">Kategorie freigeschaltet!</h3>
              </div>
              <button
                onClick={() => setShowUnlockNotification(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {newlyUnlockedCategories.map((category) => (
              <div key={category.id} className="mb-3 last:mb-0">
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: category.color }}
                  >
                    {category.icon || category.title[0]}
                  </div>
                  <div>
                    <h4 className="font-semibold">{category.title}</h4>
                    <p className="text-sm text-green-100">{category.description}</p>
                  </div>
                </div>
              </div>
            ))}
            
            <div className="mt-4 text-center">
              <p className="text-sm text-green-100">
                Herzlichen Glückwunsch! Sie können nun mit der nächsten Kategorie beginnen.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
